// ─── Image Service ─────────────────────────────────────────────────────────────
// Fetches curated photography for destinations via the production image worker.
// Endpoint: GET /api/images?query=<q>&count=<n>
// Returns: { images: [{ url, thumb, alt, photographer, photographerUrl, downloadLocation }] }
// The key lives server-side. In dev (no worker) or when unconfigured, the endpoint
// either 404s or returns { images: [] } — every caller must degrade gracefully to
// the existing gradient/emoji look. No broken <img> icons, no layout jumps.

export interface Img {
  url: string
  thumb: string
  alt: string
  photographer: string
  photographerUrl: string
  downloadLocation?: string
}

interface Destination {
  imageQuery: string
}

const cache = new Map<string, Img[]>()
const inflight = new Map<string, Promise<Img[]>>()

export async function fetchImages(query: string, count = 4): Promise<Img[]> {
  if (!query) return []
  const key = `${query}::${count}`
  if (cache.has(key)) return cache.get(key)!
  if (inflight.has(key)) return inflight.get(key)!

  const promise = (async (): Promise<Img[]> => {
    try {
      const res = await fetch(`/api/images?query=${encodeURIComponent(query)}&count=${count}`)
      if (!res.ok) return []
      const data = await res.json()
      const images: Img[] = Array.isArray(data?.images) ? data.images : []
      cache.set(key, images)
      return images
    } catch {
      // No worker available in dev, or network failure — fail silently.
      cache.set(key, [])
      return []
    } finally {
      inflight.delete(key)
    }
  })()

  inflight.set(key, promise)
  return promise
}

export async function getDestinationImages(dest: Destination, count = 4): Promise<Img[]> {
  return fetchImages(dest.imageQuery, count)
}
